import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GraduationCap } from "@phosphor-icons/react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Auto-redirect once authenticated (covers both fresh sign-in and returning sessions)
  useEffect(() => {
    if (user && user !== false) {
      // `?next=` (set by the global 401 interceptor when a session expires)
      // takes priority over location.state.from
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      const fromPath = nextParam || location.state?.from?.pathname;
      const target =
        user.role === "admin"
          ? fromPath && fromPath.startsWith("/admin")
            ? fromPath
            : "/admin"
          : fromPath && !fromPath.startsWith("/admin")
            ? fromPath
            : "/dashboard";
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state, location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    // Redirect happens via useEffect above when user state updates
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 lg:p-16">
        <Link to="/" className="flex items-center gap-3 w-fit" data-testid="login-logo-link">
          <div className="w-9 h-9 bg-[#E25822] flex items-center justify-center">
            <GraduationCap size={22} weight="bold" color="#FFFFFF" />
          </div>
          <span className="font-heading text-lg font-semibold tracking-tight">UzviS Learning</span>
        </Link>

        <div className="max-w-md w-full mx-auto lg:mx-0 page-fade">
          <div className="eyebrow mb-3">Uzvi Services • Employee Portal</div>
          <h1 className="font-heading text-4xl lg:text-5xl font-semibold tracking-tight leading-none mb-3">
            Welcome back.
          </h1>
          <p className="text-[#5C5C58] mb-10">Sign in with your <span className="font-semibold text-[#1C1C19]">UzviS work email</span> to continue your training.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] font-bold text-[#3D3D38]">Password</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 rounded-lg border-[#D8D8D2] focus-visible:ring-1 focus-visible:ring-[#E25822] focus-visible:border-[#E25822]"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div
                className="text-sm text-[#993333] border-l-2 border-[#993333] pl-3 py-1 bg-red-50"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#E25822] hover:bg-[#C44E1A] text-white rounded-lg text-base font-medium"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 text-sm">
            <Link
              to="/forgot-password"
              className="text-[#5C5C58] hover:text-[#E25822] hover:underline"
              data-testid="login-forgot-link"
            >
              Forgot your password?
            </Link>
          </div>

          <div className="mt-8 text-sm text-[#5C5C58]">
            New to UzviS Learning?{" "}
            <Link to="/register" className="text-[#E25822] font-semibold hover:underline" data-testid="login-register-link">
              Create an account
            </Link>
          </div>
        </div>

        <div className="text-xs text-[#A8A8A1] mt-12">© {new Date().getFullYear()} Uzvi Services. Internal use only.</div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative bg-[#1C1C19] overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/4ad7c645-3bf7-479a-a30f-948c8de2968e/images/550f5db9f718610968e8c4413a87b44d456b29e1380cab44b8371b53c27db440.png"
          alt="Architectural sunset corporate building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1C1C19]/85 via-[#1C1C19]/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/80 mb-5">
            Uzvi Services • Learning & Development
          </div>
          <h2 className="font-heading text-4xl xl:text-5xl font-semibold tracking-tight leading-tight max-w-md">
            Grow with UzviS. One lesson at a time.
          </h2>
        </div>
      </div>
    </div>
  );
}
